import { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import db from './firebase.config';
import { savePassengerSpotify } from './passengerConnection';

const text = value => typeof value === 'string' ? value.trim() : '';
const isToken = value => Boolean(text(value)) && !['NO_ACCESS', 'NO_REFRESH'].includes(value);

function spotifyProfile(value) {
  if (!text(value?.id)) throw new Error('Invalid Spotify profile');
  return {
    id: value.id,
    name: text(value.display_name) || value.id,
    image: Array.isArray(value.images)
      ? text(value.images.find(image => /^https?:\/\//.test(text(image?.url)))?.url) : '',
    email: text(value.email),
    uri: text(value.uri) || `spotify:user:${value.id}`,
  };
}

export default function useSpotifyConnection({ passengerId, passengerSessionId, ready, attempt = 0 }) {
  const [cookies, setCookie, removeCookie] = useCookies(['access_token', 'refresh_token']);
  const accessToken = text(cookies.access_token);
  const refreshToken = text(cookies.refresh_token);
  const dispatch = useDispatch();
  const [verified, setVerified] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const expired = useRef(false);
  const saves = useRef(new Map());

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setVerified(null);
    dispatch({ type: 'Pax_Refresh', payload: { pax_refresh: '' } });
    if (!isToken(accessToken)) {
      setFetching(false);
      if (!expired.current) setProfileError('');
      return () => { active = false; controller.abort(); };
    }
    expired.current = false;
    setProfileError('');
    setFetching(true);
    async function connect() {
      try {
        const response = await fetch('https://api.spotify.com/v1/me', {
          headers: { Authorization: `Bearer ${accessToken}` }, signal: controller.signal,
        });
        if (!active) return;
        if (response.status === 401) {
          expired.current = true;
          for (const name of ['access_token', 'refresh_token', 'pax_name', 'pax_img']) {
            removeCookie(name, { path: '/' });
            if (window.location.hostname === 'remote.strive.fm') {
              removeCookie(name, { path: '/', domain: 'remote.strive.fm' });
            }
          }
          setProfileError('Spotify connection expired. Connect again.');
          return;
        }
        if (!response.ok) throw new Error('Spotify profile unavailable');
        const profile = spotifyProfile(await response.json());
        if (!active) return;
        if (!isToken(refreshToken)) {
          setProfileError('Spotify connection is incomplete. Connect again.');
          return;
        }
        setCookie('pax_name', profile.name, { path: '/' });
        setCookie('pax_img', profile.image, { path: '/' });
        setCookie('guest_pax_name', '', { path: '/' });
        setVerified({ accessToken, refreshToken, profile });
        // Preserve the existing Redux contract: this field holds the access token.
        dispatch({ type: 'Pax_Refresh', payload: { pax_refresh: accessToken } });
      } catch (error) {
        if (active) setProfileError('Couldn’t connect to Spotify. Try again.');
      } finally {
        if (active) setFetching(false);
      }
    }
    connect();
    return () => { active = false; controller.abort(); };
  }, [accessToken, refreshToken, attempt, dispatch, setCookie, removeCookie]);

  const connected = Boolean(verified && verified.accessToken === accessToken && verified.refreshToken === refreshToken);
  useEffect(() => {
    let active = true;
    setSaveError('');
    setSaving(false);
    if (!connected || !ready || !passengerId || !passengerSessionId) return;
    const key = JSON.stringify([passengerId, passengerSessionId, accessToken, refreshToken]);
    // Reuse an in-flight or successful save across readiness/profile renders.
    if (!saves.current.has(key)) {
      const pending = savePassengerSpotify(db, {
        passengerId, passengerSessionId, profile: verified.profile, accessToken, refreshToken,
      });
      saves.current.set(key, pending);
      pending.catch(() => saves.current.delete(key));
    }
    setSaving(true);
    saves.current.get(key).then(() => {
      if (active) setSaving(false);
    }, () => {
      if (active) {
        setSaving(false);
        setSaveError('Couldn’t save your Spotify connection. Try again.');
      }
    });
    return () => { active = false; };
  }, [connected, ready, passengerId, passengerSessionId, accessToken, refreshToken, verified, attempt]);

  return { error: profileError || saveError, checking: fetching || saving, connected };
}
